<template>
  <div>
    <template v-if="auth.isLoggedIn">
      <div class="app-root">
        <header class="app-topbar">
          <div class="app-brand">
            <img src="/images/logo.png" alt="Haven" class="app-logo" />
            <span class="app-title">Haven Connections</span>
          </div>

          <nav class="app-nav" role="navigation" aria-label="Main navigation">
             <div 
               v-for="(item, index) in primeVueMenuItems" 
               :key="item.label"
               class="nav-item"
               :class="{ 'has-submenu': item.items, 'nav-item--open': navOpenIndex === index }"
             >
                <div 
                  class="nav-item-trigger"
                  tabindex="0"
                  role="button"
                  :aria-expanded="navOpenIndex === index ? 'true' : 'false'"
                  :aria-haspopup="item.items ? 'true' : 'false'"
                  @click.stop="item.items ? toggleNav(index) : (item.command ? item.command() : null)"
                  @keydown.enter.prevent="item.items ? toggleNav(index) : (item.command ? item.command() : null)"
                  @keydown.space.prevent="item.items ? toggleNav(index) : (item.command ? item.command() : null)"
                >
                  <i :class="item.icon" class="nav-icon"></i>
                  <span class="nav-label">{{ item.label }}</span>
                  <i v-if="item.items" class="pi pi-chevron-down nav-chevron" :style="{ transform: navOpenIndex === index ? 'rotate(180deg)' : '' }"></i>
                </div>
               <div v-if="item.items" class="nav-submenu" :style="{ opacity: navOpenIndex === index ? 1 : 0, visibility: navOpenIndex === index ? 'visible' : 'hidden', transform: navOpenIndex === index ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(4px)' }">
<div 
                    v-for="sub in item.items" 
                    :key="sub.label"
                    class="nav-submenu-item"
                    @click.stop="sub.command(); navOpenIndex = -1"
                  >
                   <i :class="sub.icon" class="nav-sub-icon"></i>
                   <span>{{ sub.label }}</span>
                 </div>
               </div>
             </div>
          </nav>

          <div class="topbar-actions">
            <div class="notification-trigger" @click.stop="toggleNotificationsPanel">
              <span class="p-overlay-badge">
                <Button 
                  icon="pi pi-bell" 
                  class="action-btn"
                  severity="secondary"
                  text
                  rounded
                />
                <Badge 
                  v-if="notificationCount > 0" 
                  :value="notificationCount" 
                  severity="danger"
                  class="notif-badge"
                />
              </span>
            </div>

            <div class="topbar-divider"></div>

            <div class="user-menu-trigger" @click.stop="toggleUserMenu">
              <div class="user-avatar">
                <span>{{ userInitials }}</span>
              </div>
              <div class="user-info">
                <span class="user-name">{{ auth.userName || 'User' }}</span>
                <span class="user-role">{{ auth.user?.role || '' }}</span>
              </div>
              <i class="pi pi-chevron-down user-chevron"></i>
            </div>
          </div>
        </header>

        <!-- Main Content -->
        <main class="layout-main-container">
          <div class="layout-main">
            <router-view />
          </div>
          <footer class="layout-footer">
            <span>&copy; {{ new Date().getFullYear() }} Haven Connections</span>
          </footer>
        </main>
      </div>
    </template>
    <template v-else>
      <router-view />
    </template>

    <!-- Notifications Panel -->
    <OverlayPanel 
      ref="notificationsPanel" 
      class="notification-panel"
      :appendTo="'body'"
      @show="onPanelShow"
      @hide="onPanelHide"
    >
      <div class="notif-panel-header">
        <h3>Notifications</h3>
        <Button 
          icon="pi pi-times" 
          text
          rounded
          severity="secondary"
          @click="hideNotificationsPanel"
        />
      </div>
      <div class="notif-panel-body">
        <div v-if="notifications.length === 0" class="notif-empty">
          <i class="pi pi-inbox"></i>
          <p>No new notifications</p>
        </div>
        <div 
          v-for="(notification, index) in notifications" 
          :key="notification.id" 
          class="notif-item"
          :class="{ 'unread': !notification.read }"
          @click="goToNotification(notification)"
          tabindex="0"
          role="button"
          :aria-label="`Notification: ${notification.title}`"
          @keydown.enter.prevent="goToNotification(notification)"
        >
          <div class="notif-content">
            <div class="notif-title">{{ notification.title }}</div>
            <div class="notif-message">{{ notification.message }}</div>
            <div class="notif-time">{{ formatTime(notification.time) }}</div>
          </div>
          <Button 
            icon="pi pi-times" 
            text
            rounded
            severity="secondary"
            size="small"
            class="notif-dismiss" 
            @click.stop="dismissNotification(index)"
          />
        </div>
      </div>
      <div class="notif-panel-footer">
        <Button 
          label="Mark all read" 
          text
          size="small"
          @click="markAllAsRead"
          :disabled="notificationCount === 0"
        />
        <Button 
          label="Clear all" 
          text
          size="small"
          severity="danger"
          @click="clearAllNotifications"
          :disabled="notifications.length === 0"
        />
      </div>
    </OverlayPanel>

    <!-- User Menu Panel -->
    <OverlayPanel 
      ref="userMenuPanel" 
      class="user-menu-panel"
      :appendTo="'body'"
    >
      <div class="user-menu-header">
        <div class="user-menu-avatar">
          <span>{{ userInitials }}</span>
        </div>
        <div class="user-menu-details">
          <div class="user-menu-name">{{ auth.userName || 'User' }}</div>
          <div class="user-menu-email">{{ auth.userEmail || '' }}</div>
          <div class="user-menu-role-badge">
            <Tag :severity="auth.user?.role === 'Admin' ? 'danger' : 'info'" :value="auth.user?.role || 'User'" />
          </div>
        </div>
      </div>
      <div class="user-menu-body">
        <div class="user-menu-item" @click="goToAccount">
          <i class="pi pi-user"></i>
          <span>My Account</span>
        </div>
        <div class="user-menu-item" @click="goToSettings">
          <i class="pi pi-cog"></i>
          <span>Settings</span>
        </div>
      </div>
      <div class="user-menu-footer">
        <div class="user-menu-item logout-item" @click="handleLogout">
          <i class="pi pi-sign-out"></i>
          <span>Sign Out</span>
        </div>
      </div>
    </OverlayPanel>

    <!-- Page display dialog -->
    <Dialog class="page-dialog" :breakpoints="{'960px': '50vw', '640px': '95vw'}" :style="{width: pageDialog.width, maxWidth: pageDialog.maxWidth}" :modal="!pageDialog.seamless" :maximized="pageDialog.maximized" :dismissableMask="!pageDialog.persistent" :position="pageDialog.position" v-model:visible="pageDialog.showDialog" :showHeader="false">
      <Button v-if="pageDialog.closeBtn" @click="pageDialog.showDialog=false" style="position:absolute;right:10px;top:10px" icon="pi pi-times" text rounded severity="secondary" />
      
      <component v-if="pageDialog.showDialog" is-sub-page :is="dialogComponentFile" :api-path="pageDialog.pageUrl" :page-data="pageDialog.pageData" />
    </Dialog>
    
    <!-- image preview dialog-->
    <Dialog class="image-dialog" header="Preview" :showHeader="true" :breakpoints="{'960px': '40vw', '640px': '95vw'}" style="width:auto" v-model:visible="imageDialog.showDialog" dismissableMask modal>
      <div class="text-center">
        <Carousel v-if="imageDialog.images.length > 1" :value="imageDialog.images" :circular="false" :page="imageDialog.currentSlide">
          <template #item="{data}">
            <div class="text-center">
              <NiceImage style="max-width:100%;max-height:70vh" :src="data" />
            </div>
          </template>
        </Carousel>
        <NiceImage v-else style="max-width:100%;max-height:70vh" :src="imageDialog.images[0]" />
      </div>
    </Dialog>
    
    <!-- request error dialog -->
    <Dialog class="error-dialog" modal v-model:visible="errorDialog" :breakpoints="{'960px': '50vw', '640px': '95vw'}" style="width:30vw;" position="top">
      <template #header>
        <div class="flex align-items-center gap-2">
          <Avatar size="large" style="background: rgba(239,68,68,0.15); color: #ef4444;" icon="pi pi-exclamation-triangle" />
          <span class="text-lg font-semibold" style="color: #ef4444;">Request Failed</span>
        </div>
      </template>
      <div class="text-color-secondary font-medium" v-for="(msg, index) in pageErrors" :key="index">
        {{msg}}
      </div>
    </Dialog>
    
    <ConfirmDialog />
    <Toast position="top-right" />
    
    <!-- fullscreen loading -->
    <Dialog class="loading-dialog" :showHeader="false" modal v-model:visible="fullScreenLoading" :breakpoints="{'960px': '50vw', '640px': '95vw'}" style="width:20vw;" position="center">
      <ProgressBar mode="indeterminate" style="height: 4px" />
      <div class="mt-3 text-center text-color-secondary text-sm">{{ fullScreenLoadingMsg }}</div>
    </Dialog>
  </div>
</template>

<script setup>
import { defineAsyncComponent, ref, toRefs, computed, onMounted, onUnmounted } from "vue";
import { useApp } from 'src/composables/app';
import { useAuth } from 'src/composables/auth';
import { useRoute, useRouter } from 'vue-router'; 
import { useAppStore } from 'src/store/app';
import OverlayPanel from 'primevue/overlaypanel';
import Badge from 'primevue/badge';
import Tag from 'primevue/tag';
import { ApiService } from "./services/api";

const app = useApp();
const auth = useAuth();
const store = useAppStore();
const router = useRouter();
const notifications = ref([]);
const notificationCount = ref(0);
const notificationsPanel = ref();
const userMenuPanel = ref();
const navOpenIndex = ref(-1);
const { pageDialog, imageDialog, rightDrawer, pageErrors, fullScreenLoading, fullScreenLoadingMsg } = toRefs(store);
const dialogComponentFile = computed(() => {
  if(pageDialog.value.showDialog && pageDialog.value.pageComponent){
    return defineAsyncComponent(() => import(`src/pages/${pageDialog.value.pageComponent}.vue`));
  }
  return null;
});

const drawerComponentFile = computed(() => {
  if(rightDrawer.value.showDrawer && rightDrawer.value.pageComponent){
    return defineAsyncComponent(() => import(`src/pages/${rightDrawer.value.pageComponent}.vue`));
  }
  return null;
});

const errorDialog = computed ({
  get() {
    return pageErrors.value.length > 0;
  },
  set(newValue) {
    pageErrors.value = [];
  }
});

const toggleNotificationsPanel = (event) => {
  if (notificationsPanel.value) {
    notificationsPanel.value.toggle(event);
  }
};

const toggleUserMenu = (event) => {
  if (userMenuPanel.value) {
    userMenuPanel.value.toggle(event);
  }
};

const toggleNav = (index) => {
  navOpenIndex.value = navOpenIndex.value === index ? -1 : index;
};

const handleNavClickOutside = (event) => {
  const navElement = document.querySelector('.app-nav');
  if (navElement && !navElement.contains(event.target) && navOpenIndex.value !== -1) {
    navOpenIndex.value = -1;
  }
};

onMounted(async () => {
  document.addEventListener('click', handleNavClickOutside);
  if (auth.isLoggedIn) {
    getNotifications();
    startNotificationPolling();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleNavClickOutside);
  if (notificationPollingInterval) {
    clearInterval(notificationPollingInterval);
  }
});

const handleNavClick = (index, event) => {
  const item = primeVueMenuItems.value[index];
  if (!item.items) {
    navOpenIndex.value = -1;
  }
  else if (navOpenIndex.value !== index) {
    toggleNav(index);
  }
};

const userInitials = computed(() => {
  const name = auth.userName || auth.user?.username || '';
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
});

const goToAccount = () => {
  if (userMenuPanel.value) userMenuPanel.value.hide();
  router.push('/account');
};

const goToSettings = () => {
  if (userMenuPanel.value) userMenuPanel.value.hide();
  router.push('/appsettings');
};

const handleLogout = () => {
  if (userMenuPanel.value) userMenuPanel.value.hide();
  auth.logout();
  router.push('/');
};

const hideNotificationsPanel = () => {
  if (notificationsPanel.value) {
    notificationsPanel.value.hide();
  }
};

const onPanelShow = () => {};
const onPanelHide = () => {};

const formatTime = (date) => {
  const now = new Date();
  const diff = now - date;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  
  if (diff < minute) return 'Just now';
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  return `${Math.floor(diff / day)}d ago`;
};

onMounted(async () => {
  if (auth.isLoggedIn) {
    getNotifications();
    startNotificationPolling();
  }
});

onUnmounted(() => {
  if (notificationPollingInterval) {
    clearInterval(notificationPollingInterval);
  }
});

async function getNotifications() {
  try {
    const response = await ApiService.get("alerts/index/is_read/0");
    const alerts = response.data;
    notifications.value = alerts.records.map(alert => ({
      id: alert.id,
      title: alert.title,
      message: alert.message,
      time: new Date(alert.created_at),
      read: alert.is_read,
      link: alert.alert_link || alert.link || null,
    }));
    notificationCount.value = alerts.totalRecords;
  } catch (error) {
    console.error("Error fetching notifications:", error);
  }
}

  let notificationPollingInterval;
const startNotificationPolling = () => {
  notificationPollingInterval = setInterval(getNotifications, 30000);
};

const markAsRead = async (notificationId) => {
  try {
    await ApiService.post(`alerts/mark_as_read/${notificationId}`);
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

const dismissNotification = async (index) => {
  const notification = notifications.value[index];
  await markAsRead(notification.id);
  notifications.value.splice(index, 1);
};

const markAllAsRead = async () => {
  try {
    await ApiService.post("alerts/mark_all_as_read");
  } catch (error) {
    console.error("Error marking all as read:", error);
  }
  getNotifications();
};

const clearAllNotifications = async () => {
  try {
    await ApiService.post("alerts/clear_all");
  } catch (error) {
    console.error("Error clearing notifications:", error);
  }
  notifications.value = [];
  notificationCount.value = 0;
};

const goToNotification = async (notification) => {
  await markAsRead(notification.id);
  hideNotificationsPanel();
  router.push('/alerts');
};

const primeVueMenuItems = ref([
  { 
    label: 'Home', 
    icon: 'pi pi-home',
    command: () => router.push('/home'),
  },
  {
    label: 'Property',
    icon: 'pi pi-building',
    items: [
      { label: 'Houses', icon: 'pi pi-home', command: () => router.push('/houses') },
      { label: 'Meters', icon: 'pi pi-tachometer', command: () => router.push('/meters') },
      { label: 'Readings', icon: 'pi pi-file-edit', command: () => router.push('/meterreadings') },
      { label: 'Tariffs', icon: 'pi pi-money-bill', command: () => router.push('/tariffs') }
    ]
  },
  {
    label: 'Customers',
    icon: 'pi pi-users',
    items: [
      { label: 'Customers', icon: 'pi pi-user', command: () => router.push('/customers') },
      { label: 'Tenancy', icon: 'pi pi-file-contract', command: () => router.push('/tenancy') },
      { label: 'Accounts', icon: 'pi pi-wallet', command: () => router.push('/customeraccount') }
    ]
  },
  {
    label: 'Billing',
    icon: 'pi pi-credit-card',
    items: [
      { label: 'Billing Periods', icon: 'pi pi-calendar', command: () => router.push('/billingperiods') },
      { label: 'Payments', icon: 'pi pi-dollar', command: () => router.push('/payments') },
      { label: 'Water Bills', icon: 'pi pi-drop', command: () => router.push('/waterbills') },
      { label: 'Rent Bills', icon: 'pi pi-home', command: () => router.push('/rentbills') },
    ]
  },
  {
    label: 'Deposits',
    icon: 'pi pi-wallet',
    items: [
      { label: 'Deposits refunds', icon: 'pi pi-calendar', command: () => router.push('/depositrefunds') },
      { label: 'Deposits', icon: 'pi pi-wallet', command: () => router.push('/deposits') }
    ]
  },
  {
    label: 'Messages',
    icon: 'pi pi-envelope',
    items: [
      { label: 'All Messages', icon: 'pi pi-list', command: () => router.push('/messages') },
      { label: 'Compose SMS', icon: 'pi pi-send', command: () => router.push('/messages/compose') }
    ]
  },
  {
    label: 'Settings',
    icon: 'pi pi-cog',
    items: [
      { label: 'Application Settings', icon: 'pi pi-sliders-h', command: () => router.push('/appsettings') },
      { label: 'PayBill Config', icon: 'pi pi-mobile', command: () => router.push('/payment-config/paybill') },
      { label: 'User Management', icon: 'pi pi-users', command: () => router.push('/users') },
      { label: 'System Logs', icon: 'pi pi-history', command: () => router.push('/systemlogs') }
    ]
  }
]);
</script>

<style scoped>
/* ========== ROOT LAYOUT ========== */
.app-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--surface-ground);
}

/* ========== TOPBAR ========== */
.app-topbar {
  display: flex;
  align-items: center;
  height: 48px;
  min-height: 48px;
  background: var(--surface-card);
  border-bottom: 1px solid var(--surface-border);
  position: relative;
  z-index: 1000;
  user-select: none;
  padding: 0 8px 0 0;
}

/* Brand */
.app-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 0 12px;
  flex-shrink: 0;
}

.app-logo {
  height: 22px;
  width: 22px;
  border-radius: 4px;
  object-fit: contain;
}

.app-title {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-color);
  letter-spacing: 0.02em;
}

/* Navigation */
.app-nav {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  overflow: visible;
}

.nav-item {
  position: relative;
}

.nav-item-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-color-secondary);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s ease;
}

.nav-item-trigger:hover {
  background: var(--surface-hover);
  color: var(--text-color);
}

.nav-icon {
  font-size: 14px;
}

.nav-chevron {
  font-size: 10px;
  transition: transform 0.2s ease;
}

/* Submenu */
.nav-submenu {
  position: absolute;
  top: 100%;
  left: 50%;
  margin-top: 4px;
  min-width: 180px;
  background: var(--surface-overlay);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(4px);
  z-index: 1001;
  transition: opacity 0.15s ease, visibility 0.15s ease, transform 0.15s ease;
}

.nav-item:hover .nav-submenu,
.nav-item--open .nav-submenu {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.nav-submenu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text-color-secondary);
  transition: all 0.15s ease;
  white-space: nowrap;
}

.nav-submenu-item:hover {
  background: var(--surface-hover);
  color: var(--text-color);
}

.nav-sub-icon {
  font-size: 14px;
  width: 18px;
  text-align: center;
}

/* Topbar Actions */
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  width: 34px !important;
  height: 34px !important;
}

.action-btn :deep(.p-button-icon) {
  font-size: 16px;
  color: var(--text-color-secondary);
}

.topbar-divider {
  width: 1px;
  height: 24px;
  background: var(--surface-border);
  margin: 0 4px;
}

/* User Menu Trigger */
.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.user-menu-trigger:hover {
  background: var(--surface-hover);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-600) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-avatar span {
  font-size: 13px;
  font-weight: 700;
  color: var(--primary-color-text);
}

.user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color);
}

.user-role {
  font-size: 11px;
  color: var(--text-color-secondary);
}

.user-chevron {
  font-size: 10px;
  color: var(--text-color-secondary);
  margin-left: 2px;
}

.notif-badge {
  transform: translate(40%, -40%) !important;
  font-size: 10px !important;
  min-width: 16px !important;
  height: 16px !important;
  line-height: 16px !important;
}

/* ========== MAIN CONTENT ========== */
.layout-main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface-ground);
}

.layout-main {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
}

.auth-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ========== FOOTER ========== */
.layout-footer {
  height: 32px;
  padding: 0 16px;
  background: var(--surface-card);
  border-top: 1px solid var(--surface-border);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 11px;
  color: var(--text-color-secondary);
  flex-shrink: 0;
}

/* ========== NOTIFICATIONS ========== */
.notification-panel {
  width: 380px;
}

.notification-panel :deep(.p-overlaypanel-content) {
  padding: 0;
}

.notif-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--surface-border);
}

.notif-panel-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.notif-panel-body {
  max-height: 360px;
  overflow-y: auto;
  padding: 8px;
}

.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: var(--text-color-secondary);
  gap: 8px;
}

.notif-empty i {
  font-size: 24px;
  opacity: 0.5;
}

.notif-empty p {
  margin: 0;
  font-size: 13px;
}

.notif-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 0.15s ease;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.notif-item:hover {
  background: var(--surface-hover);
}

.notif-item.unread {
  background: var(--highlight-bg);
}

.notif-content {
  flex: 1;
  min-width: 0;
}

.notif-title {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-color);
  margin-bottom: 2px;
}

.notif-message {
  font-size: 12px;
  color: var(--text-color-secondary);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notif-time {
  font-size: 11px;
  color: var(--text-color-secondary);
  margin-top: 4px;
  opacity: 0.7;
}

.notif-dismiss {
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.notif-item:hover .notif-dismiss {
  opacity: 1;
}

.notif-panel-footer {
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  border-top: 1px solid var(--surface-border);
}

/* ========== USER MENU PANEL ========== */
.user-menu-panel {
  width: 260px;
}

.user-menu-panel :deep(.p-overlaypanel-content) {
  padding: 0;
}

.user-menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--surface-border);
}

.user-menu-avatar {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-600) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-menu-avatar span {
  font-size: 16px;
  font-weight: 700;
  color: var(--primary-color-text);
}

.user-menu-details {
  flex: 1;
  min-width: 0;
}

.user-menu-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-email {
  font-size: 12px;
  color: var(--text-color-secondary);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-role-badge {
  margin-top: 4px;
}

.user-menu-role-badge :deep(.p-tag) {
  font-size: 10px;
  padding: 1px 6px;
}

.user-menu-body {
  padding: 8px;
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  font-size: 13px;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.user-menu-item:hover {
  background: var(--surface-hover);
  color: var(--text-color);
}

.user-menu-item .pi {
  font-size: 14px;
  width: 18px;
  text-align: center;
}

.user-menu-footer {
  padding: 8px;
  border-top: 1px solid var(--surface-border);
}

.logout-item:hover {
  color: #ef4444;
}

/* ========== DIALOGS ========== */
.page-dialog {
  padding: 0;
}

.page-dialog :deep(.p-dialog-content) {
  padding: 0;
}

.loading-dialog :deep(.p-dialog-content) {
  padding: 24px;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 768px) {
  .app-title {
    display: none;
  }
  
  .nav-label {
    display: none;
  }

  .nav-chevron {
    display: none;
  }
  
  .nav-item-trigger {
    padding: 6px 10px;
  }
   
  .user-info {
    display: none;
  }

  .user-chevron {
    display: none;
  }

  .topbar-divider {
    display: none;
  }
}
</style>
